import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import withStyles from 'isomorphic-style-loader/withStyles';
import { useDispatch, useSelector } from "react-redux";
import { Field, formValueSelector } from "redux-form";
import Button from 'react-bootstrap/lib/Button';
import cx from 'classnames';

import { change } from 'redux-form';

import InputFieldComponent from "../Common/FormField/InputFieldComponent";
import SelectFieldComponent from "../Common/FormField/SelectFieldComponent";
import CountryList from "../CountryList/CountryList";
import PhoneVerificationModal from "../PhoneVerificationModal/PhoneVerificationModal";
import ProfileUpload from "../../helpers/EditProfileInfo/profileUpload";

import messages from "../../locale/messages";
import PopulateData from '../../helpers/populateData';
import { getSpecificConfig } from "../../helpers/getConfigValue";

import s from './CommonFields.css';
import bt from '../../components/commonStyle.css';

const selector = formValueSelector('EditProfileInfo');

const CommonFields = ({ initialValues }) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();

    const siteSettingStatus = useSelector(state => state?.siteSettings?.data);
    const isAdmin = useSelector(state => state?.runtime?.isAdminAuthenticated);
    const picture = useSelector(state => selector(state, 'picture'));
    const userId = initialValues?.userId;

    const [dateOfBirthData, setDateOfBirthData] = useState({});
    const [countryCode, setCountryCode] = useState('US');
    const [country, setCountry] = useState('+1');
    const [maxUploadSize, setMaxUploadSize] = useState('');

    useEffect(() => {
        if (initialValues?.countryCode) {
            setCountryCode(initialValues?.countryName || 'US');
            setCountry(initialValues?.countryCode || '+1');
        }

        const initDateData = () => {
            const now = new Date();
            const currentYear = now.getFullYear();
            const years = PopulateData.generateData(1920, currentYear, "desc");
            const days = PopulateData.generateData(1, 31);
            const months = PopulateData.generateData(0, 11);
            setDateOfBirthData({ years, months, days });
        };

        const handleMaxUploadFile = async () => {
            const siteData = await getSpecificConfig({ name: ['maxUploadSize'] });
            setMaxUploadSize(siteData.maxUploadSize);
        };

        initDateData();
        handleMaxUploadFile();
    }, [initialValues]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        dispatch(change('EditProfileForm', name, value));
    };

    const handleCountryChange = (e, selectedData) => {
        setCountry(selectedData.dialCode);
        setCountryCode(selectedData.countryCode);
        dispatch(change('EditProfileForm', 'phoneCountryCode', selectedData.countryCode));
        dispatch(change('EditProfileForm', 'dialCode', selectedData.dialCode));
    };

    const genderValue = [
        { value: '', label: formatMessage(messages.gender) },
        { value: "Male", label: formatMessage(messages.genderMale) },
        { value: "Female", label: formatMessage(messages.genderFemale) },
        { value: 'Other', label: formatMessage(messages.genderOther) }
    ];

    const generateSelectOptions = (labelMsg, items, formatter) => [
        { value: '', label: formatMessage(labelMsg) },
        ...(items?.map(item => ({
            value: item,
            label: formatter ? formatter(item) : item
        })) || [])
    ];

    const monthValue = generateSelectOptions(messages.month, dateOfBirthData.months, item => item + 1);
    const daysValue = generateSelectOptions(messages.transferDate, dateOfBirthData.days);
    const yearsValue = generateSelectOptions(messages.year, dateOfBirthData.years);

    const isPhoneStatus = siteSettingStatus?.phoneNumberStatus == 1;

    return (
        <div className={isAdmin && s.editMainContainer}>
            {isAdmin && (
                <div className={s.userAvatarSection}>
                    <ProfileUpload
                        picture={picture}
                        userId={userId}
                        data={initialValues}
                        maxUploadSize={maxUploadSize}
                        isAdmin={isAdmin}
                    />
                </div>
            )}
            <div className={s.displayGrid}>
                <Field
                    name="firstName"
                    type="text"
                    component={InputFieldComponent}
                    label={formatMessage(messages.firstName)}
                    placeholder={formatMessage(messages.firstNamePlaceHolder)}
                    inputClass={bt.commonControlInput}
                    formGroupClass={bt.space4}
                    infoText={formatMessage(messages.lastNameInfo)}
                    showToolTip
                />
                <Field
                    name="lastName"
                    type="text"
                    component={InputFieldComponent}
                    label={formatMessage(messages.lastName)}
                    placeholder={formatMessage(messages.lastNamePlaceHolder)}
                    inputClass={bt.commonControlInput}
                    formGroupClass={bt.space4}
                />
                <Field
                    name="gender"
                    label={formatMessage(messages.gender)}
                    inputClass={cx(bt.commonControlSelect, 'commonControlSelectRtl')}
                    formGroupClass={bt.space4}
                    component={SelectFieldComponent}
                    options={genderValue}
                />
                <div className={cx(s.formGroup)} >
                    <label className={cx(bt.commonLabelText)}>{formatMessage(messages.dateOfBirth)}</label>
                    <div className={s.birthDayFlex}>
                        <Field
                            name="month"
                            inputClass={cx(bt.commonControlSelect, 'commonControlSelectRtl')}
                            formGroupClass={bt.space4}
                            component={SelectFieldComponent}
                            options={monthValue}
                        />
                        <Field
                            name="day"
                            inputClass={cx(bt.commonControlSelect, 'commonControlSelectRtl')}
                            formGroupClass={bt.space4}
                            component={SelectFieldComponent}
                            options={daysValue}
                        />
                        <Field
                            name="year"
                            inputClass={cx(bt.commonControlSelect, 'commonControlSelectRtl')}
                            formGroupClass={bt.space4}
                            component={SelectFieldComponent}
                            options={yearsValue}
                        />
                    </div>
                </div>
            </div>
            <div>
                <div className={s.displayGrid}>
                    <Field
                        name="email"
                        type="text"
                        component={InputFieldComponent}
                        placeholder={formatMessage(messages.emailPlaceHolder)}
                        label={formatMessage(messages.email)}
                        inputClass={cx(bt.commonControlInput)}
                        formGroupClass={bt.space4}
                        disabled
                    />
                    <Field
                        name="location"
                        type="text"
                        component={InputFieldComponent}
                        label={formatMessage(messages.liveLocation)}
                        inputClass={bt.commonControlInput}
                        formGroupClass={bt.space4}
                        placeholder="e.g. Paris, France /Brooklyn, NY, IL"
                    />
                </div>
                <label className={cx(bt.commonLabelText)}>{formatMessage(messages.phoneNumber)}</label>
                <div className={s.phoneNoInputFlex}>
                    {isAdmin ? (
                        <div>
                            <CountryList
                                input={{ name: 'countryCode', onChange: handleChange, value: countryCode }}
                                className={cx(bt.commonControlSelect, bt.space4, 'commonControlSelectRtl')}
                                dialCode={false}
                                getSelected={handleCountryChange}
                                formName={'EditProfileInfo'}
                            />
                            <Field
                                name="phoneNumber"
                                type="text"
                                component={InputFieldComponent}
                                inputClass={cx(bt.commonControlInput)}
                                formGroupClass={bt.space4}
                                onChange={handleChange}
                                suffixLabel={country}
                                isAddon
                                placeholder={formatMessage(messages.phoneNumberPlaceHolder)}
                            />
                        </div>
                    ) : (
                        <div>
                            {!isPhoneStatus && (
                                <div className={s.widthredcd}>
                                    <CountryList
                                        input={{ name: 'countryCode', onChange: handleChange, value: countryCode }}
                                        className={cx(bt.commonControlSelect, bt.space4, 'commonControlSelectRtl')}
                                        dialCode={false}
                                        getSelected={handleCountryChange}
                                        formName={'EditProfileInfo'}
                                    />
                                    <Field
                                        name="phoneNumber"
                                        type="text"
                                        component={InputFieldComponent}
                                        label={formatMessage(messages.phoneNumber)}
                                        inputClass={bt.commonControlInput}
                                        formGroupClass={bt.space4}
                                        onChange={handleChange}
                                        suffixLabel={country}
                                        isAddon
                                        placeholder={formatMessage(messages.phoneNumberPlaceHolder)}
                                    />
                                </div>
                            )}
                            {isPhoneStatus ? <PhoneVerificationModal userId={userId} /> : <span></span>}
                            <p className={cx(s.noMarginBottom, bt.spaceTop1)}>{formatMessage(messages.phoneNumberInfo)}</p>
                        </div>
                    )}
                </div>

                <Field
                    name="info"
                    component={InputFieldComponent}
                    componentClass={'textarea'}
                    label={formatMessage(messages.info)}
                    inputClass={bt.commonControlTextArea}
                    formGroupClass={bt.space4}
                    placeholder={formatMessage(messages.infoPlaceHolder)}
                />
            </div>
            <div className='singleButtonFlexEnd'>
                <Button className={cx(bt.btnPrimary, bt.btnLarge, s.spaceTop3)} type="submit">
                    {formatMessage(messages.save)}
                </Button>
            </div>
        </div>
    );
};

export default withStyles(s, bt)(CommonFields);